/**
 * TikTok OAuth Callback Page
 * Handles the redirect from TikTok after user authorizes
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TikTokCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("Connecting your TikTok account...");
  const [accountName, setAccountName] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (error) {
        setStatus("error");
        setMessage(errorDescription || "TikTok authorization was denied");
        return;
      }

      if (!code || !state) {
        setStatus("error");
        setMessage("Missing authorization code or state");
        return;
      }

      try {
        const response = await apiClient.post("/social/oauth/tiktok/callback", null, {
          params: { code, state }
        });

        if (response.data.success) {
          setStatus("success");
          setAccountName(response.data.account_name || "TikTok");
          setMessage(`Successfully connected to ${response.data.account_name || "TikTok"}!`);
          toast.success("TikTok account connected!");
          
          // Redirect to settings with success params to trigger success modal
          setTimeout(() => {
            navigate(`/settings?tab=social&connected=true&platform=TikTok&account=${encodeURIComponent(response.data.account_name || "TikTok")}`);
          }, 2000);
        } else {
          setStatus("error");
          setMessage(response.data.message || "Failed to connect TikTok account");
        }
      } catch (err) {
        console.error("TikTok callback error:", err);
        setStatus("error");
        setMessage(err.response?.data?.detail || "Failed to connect TikTok account");
        toast.error("Failed to connect TikTok");
      }
    };

    if (user) {
      handleCallback();
    }
  }, [searchParams, user, navigate]);

  return (
    <div className="min-h-screen bg-viraly-dark flex items-center justify-center">
      <div className="card-base p-8 max-w-md w-full text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-16 h-16 text-viraly-orange animate-spin mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold mb-2">Connecting TikTok</h2>
            <p className="text-viraly-muted">{message}</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-viraly-success/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-viraly-success" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Connected!</h2>
            <p className="text-viraly-muted mb-4">{message}</p>
            <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-white/5 mb-6">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                <span className="text-lg">🎵</span>
              </div>
              <span className="font-medium">{accountName}</span>
            </div>
            <p className="text-xs text-viraly-muted">Redirecting to settings...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Connection Failed</h2>
            <p className="text-viraly-muted mb-6">{message}</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate("/settings?tab=social")} variant="outline">
                Go to Settings
              </Button>
              <Button onClick={() => window.location.reload()} className="btn-primary">
                Try Again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
